import { useState } from 'react';
import { Box, IconButton, Stack, Typography, Button, Grid2 } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import CustomDivider from '../../shared/utils/CustomDivider';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import ToggleSwitch from '../../shared/utils/CustomToggle';
import { postLmsTopics } from '../../assets/nodeApi/postApi/postAdminApi';
import { putLmsTopics } from '../../assets/nodeApi/putApi/putAdminApi';
import { patchTopic } from '../../assets/nodeApi/patchApi/PatchAdminApi';
import { Grid_Gap, Input_Width } from '../../shared/constants';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { queryClient } from '../../assets/nodeApi/reactQueryClient';
import { useLmsDropdownOptions } from './option/LsmOption';
import { IdAutoCompleteField } from '../../shared/components/formfields/IdAutoCompleteField';

const LmsTopic = () => {

    const { showToaster } = useToaster();
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const { lmsSubject, lmsGrade, lmsChapter, lmsTopic } = useLmsDropdownOptions(
        {
            gradeFilter: { status: true },
            charpterFilter: { status: true },
            subjectFilter: { status: "active" }
        }
    )

    const validationSchema = Yup.object().shape({
        subjectId: Yup.string().required('Subject is required'),
        gradeId: Yup.string().required('Grade is required'),
        chapterId: Yup.string().required('Chapter is required'),
        name: Yup.string().required('topic is required'),
        description: Yup.string().required('Description is required'),
    });

    const initialValues = {
        subjectId: selectedTopic?.subjectId,
        gradeId: selectedTopic?.gradeId,
        chapterId: selectedTopic?.chapterId,
        name: selectedTopic?.name,
        description: selectedTopic?.description,
    };

    const mutationAdd = useMutation({
        mutationFn: (formData) => postLmsTopics(formData),
        onSuccess: () => {
            showToaster('Topic added successfully!', '', 'success');
            queryClient.invalidateQueries(['topics']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to add Topic', '', 'error');
        },
    });


    const mutationUpdate = useMutation({
        mutationFn: ({ formData, id }) => putLmsTopics(formData, id),
        onSuccess: () => {
            showToaster('Topic updated successfully!', '', 'success');
            queryClient.invalidateQueries(['topics']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update Topic', '', 'error');
        },
    });


    const handleSubmit = (values, { resetForm }) => {

        if (isEditing) {
            mutationUpdate.mutate({ formData: values, id: selectedTopic?.id, });

        } else {
            mutationAdd.mutate(values);
        }
        resetForm({
            values: {
                gradeId: "",
                subjectId: "",
                chapterId: "",
                name: "",
                description: ""
            }
        })
        setIsEditing(false);
        setSelectedTopic({});

    };

    const toggleStatusMutation = useMutation({

        mutationFn: ({ formData, id }) => patchTopic(formData, id),
        onSuccess: (response) => {
            showToaster(`Topic ${response?.data?.status ? "Enabled" : "Disabled"} successfully!`, '', 'success');
            queryClient.invalidateQueries(['topics']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update Topic', '', 'error');
        },

    });



    const topicColumns = [
        { field: 'sr_no', headerName: 'Sr No', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'grade', headerName: 'Grade', flex: 1, renderCell: (params) => <Typography>{params.row?.lms_grades?.name}</Typography> },
        { field: 'subject', headerName: 'Subject', flex: 1, renderCell: (params) => <Typography>{params.row?.subjects?.subject}</Typography> },
        { field: 'chapter', headerName: 'Chapter', flex: 1, renderCell: (params) => <Typography>{params.row?.chapters?.name}</Typography> },
        { field: 'name', headerName: 'Topic', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'description', headerName: 'Description', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        {
            field: 'actionsEdit',
            headerName: 'Edit',
            flex: 1,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <IconButton
                        onClick={() => {
                            setSelectedTopic(params.row);
                            setIsEditing(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        size="small"
                    >
                        <EditIcon />
                    </IconButton>
                </Stack>
            ),
        },
        {
            field: 'actionsToggle',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <ToggleSwitch checked={params.row.status ? true : false}
                    onCheck={() =>
                        toggleStatusMutation.mutate({
                            formData: { status: !params.row.status ? true : false },
                            id: params.row.id
                        })
                    }
                />
            ),
        },
    ];

    return (
        <Box>
            <CustomDivider />
            <NormalContainerWithHeader headerText={isEditing ? 'Edit Topic' : 'Add Topic'}>
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                    {({ resetForm }) => (
                        <Form>
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="gradeId"
                                        component={IdAutoCompleteField}
                                        label="Select Grade"
                                        options={lmsGrade.options ?? []}
                                        isLoading={lmsGrade.isLoading}
                                        isRequired
                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="subjectId"
                                        component={IdAutoCompleteField}
                                        options={lmsSubject.options}
                                        isLoading={lmsSubject.isLoading}
                                        label="Select Subject"
                                        isRequired

                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="chapterId"
                                        component={IdAutoCompleteField}
                                        label="Select Chapter"
                                        options={lmsChapter.options ?? []}
                                        isLoading={lmsChapter.isLoading}
                                        isRequired
                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="name"
                                        component={TextFormField}
                                        label="Topic"
                                        isRequired
                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="description"
                                        component={TextFormField}
                                        label="Description"
                                        isRequired
                                    />
                                </Grid2>
                            </Grid2>


                            <CustomDivider />
                            <Grid2 container spacing={Grid_Gap} sx={{ justifyContent: "end" }} >
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        onClick={() => {
                                            resetForm({
                                                values: {
                                                    gradeId: "",
                                                    subjectId: "",
                                                    chapterId: "",
                                                    name: "",
                                                    description: ""
                                                }
                                            })
                                            setIsEditing(false);
                                            setSelectedTopic(null);
                                        }}
                                        fullWidth
                                    >
                                        {isEditing ? 'Cancel' : 'Reset'}
                                    </Button>
                                </Grid2>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        disabled={mutationAdd.isPending || mutationUpdate.isPending}
                                    >
                                        {mutationUpdate.isPending
                                            ? "Updating..."
                                            : mutationAdd.isPending
                                                ? "Submitting..."
                                                : isEditing
                                                    ? "Update"
                                                    : "Submit"}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>

            <CustomDivider />

            <TableFilterTemplate
                title="Topic List"
                columns={topicColumns}
                rowsData={lmsTopic.options || []}
                isLoading={lmsTopic.isLoading}
            />

        </Box>
    );
};

export default LmsTopic;







